import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { catchError, combineLatest, concatMap, EMPTY, filter, map, Observable, of, take, tap } from 'rxjs';
import { SidePanelService } from 'src/app/features/side-panel/services/side-panel.service';
import {
  InventoryAdditionalProperties,
  InventoryApplicationResourceProperties,
  InventoryAutomationResourceProperties,
  InventoryBotResourceProperties,
  InventoryConnectionResourceProperties,
  InventoryConnectorResourceProperties,
  InventoryDataStorageResourceProperties,
  InventoryEntityField,
  InventoryEntityFieldStatus,
  InventoryEnvironmentResourceProperties,
  InventoryIdentityResourceProperties,
  InventoryOrgWideAccessCategory,
  InventoryResourceGenericRelation,
  InventoryResourceKnownRelation,
  InventoryResourceModel,
  InventoryResourceUrl,
  InventoryDeviceResourceProperties,
  PowerPlatformCopilotActionAuthMode,
  PowerPlatformCopilotActionType,
  PowerplatformCopilotPluginType,
  RefreshMethod,
  ResourceCategory,
} from 'src/app/models/api/inventoryResource.model';
import { IInventoryResourceViewModel } from 'src/app/models/viewModels/inventoryResourceViewModel';
import { DisplayNameService } from 'src/app/services/common/displayName/displayName.service';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { InventoryHttpService } from 'src/app/services/httpServices/inventoryHttpService/inventory-http.service';
import { InventoryResourceRequestHttpErrorHandlerService } from 'src/app/services/httpServices/InventoryResourceRequestHttpErrorHandlerService/inventory-resource-request-http-error-handler.service';
import { ViolationsHttpService } from 'src/app/services/httpServices/violationsHttpService/violations-http.service';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { InventoryResourceCardFieldBuilder } from './inventoryResourceCardFieldBuilder';
import { InventoryActionsService } from 'src/app/services/actions/inventory/inventory-actions.service';
import { BeautifyDisplayStringPipe } from 'src/app/pipes/beautify-display-string/beautify-display-string.pipe';
import { DisplayCamelCaseStringPipe } from 'src/app/pipes/display-camel-case-string/display-camel-case-string.pipe';
import { DatePipe } from '@angular/common';
import { ISidepanelTab } from 'src/app/components/sidepanel-tabs/sidepanel-tabs.component';
import _ from 'lodash';
import { ViolationModel, ViolationsDataType, ViolationStatus } from 'src/app/models/api/violations.model';
import { Label } from 'src/app/labels/englishLabels';
import { Platform, ResourceType } from 'src/app/models/api';
import { AiService } from 'src/app/models/api/agentStep.model';
import { AIDR_SUPPORTED_SERVICES } from 'src/app/models/selectOptions/findingAiServiceSelectOptions.mode';
import { InventoryHelper } from '../inventory/helpers/inventoryHelper';
import { StatusLabelEnum } from 'src/app/components/status-label/status-label.component';
import { buildResourceTypeDisplayName } from 'src/app/labels/utils';
import { getResourceCategoryIconPath, getResourceCategoryDisplayName } from 'src/app/common/categoryUtils';
import { getPlatformDisplayName, getPlatformLogoPath } from 'src/app/common/platformUtils';
import { DatasetResourceAdditionalPropertiesKeys } from 'src/app/models/api/resourcesAdditionalProperties.consts';
import { JsonHalCollection } from 'src/app/models/api/jsonhal.model';
import { RelatedViolationCardComponentInput } from 'src/app/components/sidepanel-cards/related-violation-card/related-violation-card.component';
import { ViolationRowHelpers } from '../violations/helpers/violationRowHelpers';
import { ISelectOption } from 'src/app/models/selectOptions';
import { MenuAction } from 'src/app/models/menuAction.model';
import { InventorySupportedActions } from 'src/app/services/actions/inventory/inventorySupportedActions';
import { ITimelineItem } from 'src/app/components/timeline/timeline.component';
import {
  RelatedResourceCardComponentInput,
  RelatedResourceInfo,
} from 'src/app/components/sidepanel-cards/related-resource-card/related-resource-card.component';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import { beautifyCamelCaseString } from 'src/app/common/stringUtils';
import { RouterNavigationParams } from 'src/app/components/anchor-button/anchor-link.component';
import { QueryParametersModel } from 'src/app/models/viewModels/tableRowViewModel';
import { CombinedIntegration } from 'src/app/models/api/integration.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ICardField } from 'src/app/models/viewModels/cardModel';
import { InventoryToolsResourceProperties } from 'src/app/models/api/inventoryResource.model';
import { getAiServiceDisplayName, getAiServiceLogoPath } from 'src/app/common/aiServiceUtils';

const SupportedResourcesCategoriesScores = [
  ResourceCategory.automation.toString(),
  ResourceCategory.application.toString(),
  ResourceCategory.connection.toString(),
];

const RUNTIME_ACTIVITY_DISABLED_ENVIRONMENTS = ['teams', 'developer'];

@Injectable()
export class InventoryResourceService {
  private readonly _model: WritableSignal<InventoryResourceModel | null> = signal(null);
  private readonly _tabs: WritableSignal<ISidepanelTab[]> = signal([]);
  private readonly _menuActions: WritableSignal<MenuAction[]> = signal([]);

  private readonly _isPageLoading: WritableSignal<boolean> = signal<boolean>(true);
  private readonly _isRelationsTabLoading: WritableSignal<boolean> = signal<boolean>(true);
  private readonly _isViolationsTabLoading: WritableSignal<boolean> = signal<boolean>(true);
  private readonly _isHygieneTabLoading: WritableSignal<boolean> = signal<boolean>(true);

  private readonly _statusLabel: WritableSignal<StatusLabelEnum> = signal(StatusLabelEnum.active);
  private readonly _statusLabelReason: WritableSignal<string> = signal('');

  private readonly _agentLink: WritableSignal<RouterNavigationParams | null> = signal(null);
  private readonly _isAgentLinkDisabled: WritableSignal<boolean> = signal(false);
  private readonly _agentLinkDisabledTooltip: WritableSignal<string> = signal('');

  private readonly _infoCardFields: WritableSignal<ICardField[]> = signal([]);
  private readonly _additionalInfoCardFields: WritableSignal<ICardField[]> = signal([]);
  private readonly _environmentCardFields: WritableSignal<ICardField[]> = signal([]);
  private readonly _generativeAiPropertiesCardFields: WritableSignal<ICardField[]> = signal([]);
  private readonly _identitiesCardFields: WritableSignal<ICardField[]> = signal([]);

  private readonly _isLinkDisabled: WritableSignal<boolean> = signal(false);

  private readonly _relatedResourcesCardsInputs: WritableSignal<RelatedResourceCardComponentInput[]> = signal([]);
  private readonly _relatedViolationsCardsInputs: WritableSignal<RelatedViolationCardComponentInput[]> = signal([]);
  private readonly _relatedHygieneCardsInputs: WritableSignal<RelatedViolationCardComponentInput[]> = signal([]);

  private readonly _timelineItems: WritableSignal<ITimelineItem[]> = signal([]);
  private readonly _sampleRelatedResourcesDisclaimer: WritableSignal<string> = signal('');
  private readonly _totalRelatedResources: WritableSignal<number> = signal<number>(0);
  private readonly _relatedResourceNavigationQueryParams: WritableSignal<
    QueryParametersModel | Record<string, string>
  > = signal({});
  private readonly _violationsPageUrl: WritableSignal<string> = signal('');
  private readonly _resourceCategoryFilters: WritableSignal<ISelectOption[]> = signal([]);

  private _integrationName = '';
  private _pendingAgentLinkResourceId: string | null = null;

  private readonly _resourcesWithoutPlatformResourceId: string[] = [ResourceType.gateway];
  private readonly _hiddenAdditionalProperties: string[] = ['creatorIdentityType'];
  private readonly _notAvailableString: string = Label.get('common.notAvailable');
  private readonly _comingSoonString: string = Label.get('common.comingSoon');
  private readonly _persistenceBranchMerged: Date = new Date('Dec 24, 2024, 05:24:00 PM GMT+2'); // Date when #15467 was merged

  private readonly _httpClient = inject(HttpClient);
  private readonly _inventoryHttpService = inject(InventoryHttpService);
  private readonly _violationsHttpService = inject(ViolationsHttpService);
  private readonly _storeService = inject(StoreService);
  private readonly _httpErrorHandlerServiceForInventoryResourceThatWasNotFound = inject(
    InventoryResourceRequestHttpErrorHandlerService
  );
  private readonly _httpErrorHandlerService = inject(HttpErrorHandlerService);
  private readonly _displayNameService = inject(DisplayNameService);
  private readonly _featureFlagsService = inject(FeatureFlagsService);
  private readonly _sidePanelService = inject(SidePanelService);
  private readonly _resourceCardFieldBuilder = inject(InventoryResourceCardFieldBuilder);
  private readonly _inventoryActionsService = inject(InventoryActionsService);
  private readonly _beautifyDisplayStringPipe = inject(BeautifyDisplayStringPipe);
  private readonly _displayCamelCaseStringPipe = inject(DisplayCamelCaseStringPipe);
  private readonly _datePipe = inject(DatePipe);
  private readonly _destroyRef = inject(DestroyRef);

  private static readonly _RSA_MOCK_RESOURCE_IDS = new Set([
    'skyway-booking-chatbot-prod-001',
    'claude-code-eng-ws-001',
    'agentforce-lead-auto-handler-001',
    'rsa-identity-sarah-mitchell',
    'rsa-identity-david-chen',
    'rsa-identity-marketing-ops',
    'skyway-user-jake',
    'skyway-tool-flight-search',
    'skyway-tool-customer-db',
    'skyway-tool-send-email',
    'skyway-tool-pricing',
    'claude-user-david',
    'claude-mcp-postgres',
    'claude-db-prod-main',
    'agentforce-tool-read-leads',
    'agentforce-tool-edit-leads',
    'agentforce-tool-send-email',
    'agentforce-crm-lead-object',
  ]);

  private static readonly _IAM_DEMO_MOCK_RESOURCE_IDS = new Set([
    '02a1b3cd4ef56789abcde1234567890a',
    '13b7c4e2d09f1a3e8b5c6d7e4f2a1b0c',
    '24c8d5f3e10g2b4f9c6d7e5f3b2c1d0e',
    'crm-insight-agent-resource-id',
    '35d9e6g4f21h3c5g0d7e8f6g4c3d2e1f',
    '4822e539291f4bc96758c5f37bbc693f',
    '5933f640302g5cd07869d6g48ccd704g',
    'chris-smith-identity-id',
    'spaiware-chatgpt-enterprise-001',
    'spaiware-chatgpt-app-001',
    'spaiware-attachment-eoy-001',
    'spaiware-memory-001',
    'spaiware-ethan-identity-001',
    'cursor-mcp-david-chen-identity',
    'cursor-mcp-cursor-agent-session',
    'cursor-mcp-postgres-mcp-server',
    'cursor-mcp-github-mcp-server',
    'cursor-mcp-filesystem-mcp-server',
    'cursor-mcp-production-db',
    'cursor-mcp-github-repo',
    'cursor-mcp-db-service-token',
    'cursor-mcp-github-oauth-token',
    'cursor-mcp-skill-code-debug',
  ]);

  public getModel(): Signal<InventoryResourceModel | null> {
    return this._model.asReadonly();
  }
  public getTabs(): Signal<ISidepanelTab[]> {
    return this._tabs.asReadonly();
  }
  public getMenuActions(): Signal<MenuAction[]> {
    return this._menuActions.asReadonly();
  }

  public getIsPageLoading(): Signal<boolean> {
    return this._isPageLoading.asReadonly();
  }
  public getIsRelationsTabLoading(): Signal<boolean> {
    return this._isRelationsTabLoading.asReadonly();
  }
  public getIsViolationsTabLoading(): Signal<boolean> {
    return this._isViolationsTabLoading.asReadonly();
  }
  public getIsHygieneTabLoading(): Signal<boolean> {
    return this._isHygieneTabLoading.asReadonly();
  }

  public getStatusLabel(): Signal<StatusLabelEnum> {
    return this._statusLabel.asReadonly();
  }
  public getStatusLabelReason(): Signal<string> {
    return this._statusLabelReason.asReadonly();
  }

  public getAgentLink(): Signal<RouterNavigationParams | null> {
    return this._agentLink.asReadonly();
  }

  public getIsAgentLinkDisabled(): Signal<boolean> {
    return this._isAgentLinkDisabled.asReadonly();
  }

  public getAgentLinkDisabledTooltip(): Signal<string> {
    return this._agentLinkDisabledTooltip.asReadonly();
  }

  public getInfoCardFields(): Signal<ICardField[]> {
    return this._infoCardFields.asReadonly();
  }
  public getAdditionalInfoCardFields(): Signal<ICardField[]> {
    return this._additionalInfoCardFields.asReadonly();
  }
  public getEnvironmentCardFields(): Signal<ICardField[]> {
    return this._environmentCardFields.asReadonly();
  }
  public getGenerativeAiPropertiesCardFields(): Signal<ICardField[]> {
    return this._generativeAiPropertiesCardFields.asReadonly();
  }

  public getIdentitiesCardFields(): Signal<ICardField[]> {
    return this._identitiesCardFields.asReadonly();
  }

  public getIsLinkDisabled(): Signal<boolean> {
    return this._isLinkDisabled.asReadonly();
  }

  public getRelatedResourcesCardsInputs(): WritableSignal<RelatedResourceCardComponentInput[]> {
    return this._relatedResourcesCardsInputs;
  }
  public getRelatedViolationsCardsInputs(): Signal<RelatedViolationCardComponentInput[]> {
    return this._relatedViolationsCardsInputs.asReadonly();
  }
  public getRelatedHygieneCardsInputs(): Signal<RelatedViolationCardComponentInput[]> {
    return this._relatedHygieneCardsInputs.asReadonly();
  }

  public getTimelineItems(): Signal<ITimelineItem[]> {
    return this._timelineItems.asReadonly();
  }
  public getSampleRelatedResourcesDisclaimer(): Signal<string> {
    return this._sampleRelatedResourcesDisclaimer.asReadonly();
  }
  public getTotalRelatedResources(): WritableSignal<number> {
    return this._totalRelatedResources;
  }
  public getRelatedResourceNavigationQueryParams(): Signal<QueryParametersModel> {
    return this._relatedResourceNavigationQueryParams.asReadonly();
  }
  public getViolationsPageUrl(): Signal<string> {
    return this._violationsPageUrl.asReadonly();
  }
  public getResourceCategoryFilters(): Signal<ISelectOption[]> {
    return this._resourceCategoryFilters.asReadonly();
  }

  public initInventoryResourceData(
    resourceId: string,
    integrationId: string,
    resourceView?: IInventoryResourceViewModel
  ): Observable<[InventoryResourceModel, CombinedIntegration[]]> {
    const resource$ = this._isMockResource(resourceId)
      ? this._loadMockResourceById(resourceId)
      : resourceView?.resource
        ? of(resourceView?.resource)
        : this._inventoryHttpService.getInventoryResource(integrationId, resourceId);

    return combineLatest([resource$, this._storeService.integrations$]).pipe(
      filter(([resource, integrations]) => resource != null && integrations != null),
      take(1),
      tap(([resource, integrations]) => {
        const integration = _.keyBy(integrations, 'id')[integrationId];
        this._integrationName = integration ? integration.name : (resource.integration?.name ?? '');

        this._model.set(resource);

        this._relatedResourceNavigationQueryParams.set({
          q: `relatedResource:${resourceId} integration:${integrationId}`,
        });
        this._violationsPageUrl.set(`/app/violations?q=resourceId:${resourceId} violationStatus:open`);
        this._initSinglePage(resource, resourceId, integrationId, resourceView);
        this._setAgentLinkIfApplicable(resource.resourceType, resource.platformInfo.resourceId, resource.aiService);
      }),
      catchError((error) => {
        this._httpErrorHandlerServiceForInventoryResourceThatWasNotFound.handle(error);
        this._closeWindow();
        return EMPTY;
      })
    );
  }

  private _isRsaMockResource(resourceId: string): boolean {
    return (
      this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.RsaDemoRuntimeBoundaries) &&
      InventoryResourceService._RSA_MOCK_RESOURCE_IDS.has(resourceId)
    );
  }

  private _isIamDemoMockResource(resourceId: string): boolean {
    return (
      this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.SpmIamDemoAmex) &&
      InventoryResourceService._IAM_DEMO_MOCK_RESOURCE_IDS.has(resourceId)
    );
  }

  private _isMockResource(resourceId: string): boolean {
    return this._isRsaMockResource(resourceId) || this._isIamDemoMockResource(resourceId);
  }

  private _initMockViolationsTab(resourceId: string, model?: InventoryResourceModel): void {
    const isIamDemo = this._isIamDemoMockResource(resourceId);
    if (!isIamDemo) {
      this._isViolationsTabLoading.set(false);
      return;
    }

    const relatedIds = new Set(
      (model?.sampleRelations ?? []).map((r) => r.relatedResourceId)
    );
    relatedIds.add(resourceId);

    this._httpClient
      .get<ViolationModel[]>('assets/mockData/iamDemoViolationsMockData.json')
      .pipe(
        map((violations) => {
          const matched = violations.filter(
            (v) => relatedIds.has(v.resource?.id)
          );
          return {
            _embedded: { items: matched },
            _links: { self: { href: '' } },
            page: {
              number: 1,
              size: matched.length,
              totalItems: matched.length,
              totalPages: 1,
            },
          } as JsonHalCollection<ViolationModel>;
        }),
        tap((response) => this._initViolationsOrHygieneTab(response, ViolationsDataType.violations)),
        tap(() => this._isViolationsTabLoading.set(false)),
        catchError(() => {
          this._isViolationsTabLoading.set(false);
          return EMPTY;
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  private _initMockRelationsTab(integrationId: string, model: InventoryResourceModel, resourceId: string): void {
    if (!model.sampleRelations?.length) {
      this._initRelationsTab(integrationId, model);
      return;
    }

    const jsonPath = this._isIamDemoMockResource(resourceId)
      ? 'assets/mockData/iamDemoInventoryMockData.json'
      : 'assets/mockData/rsaInventoryMockData.json';

    this._httpClient
      .get<InventoryResourceModel[]>(jsonPath)
      .pipe(
        map((allResources) => {
          const resourceMap = new Map(allResources.map((r) => [r.id, r]));
          const calledByIds = new Set(
            model.sampleRelations.filter((r) => r.relationType === 'calledBy').map((r) => r.relatedResourceId)
          );
          const relatedIds = new Set(model.sampleRelations.map((r) => r.relatedResourceId));
          const firstLevel = allResources.filter((r) => relatedIds.has(r.id));
          firstLevel.forEach((r) => {
            if (calledByIds.has(r.id)) {
              return;
            }
            r.sampleRelations?.forEach((rel) => {
              if (
                rel.relatedResourceId !== resourceId &&
                !relatedIds.has(rel.relatedResourceId) &&
                resourceMap.has(rel.relatedResourceId)
              ) {
                relatedIds.add(rel.relatedResourceId);
                model.sampleRelations.push(rel);
              }
            });
          });
          const seen = new Set<string>();
          model.sampleRelations = model.sampleRelations.filter((rel) => {
            if (seen.has(rel.relatedResourceId)) {
              return false;
            }
            seen.add(rel.relatedResourceId);
            return true;
          });

          const items = allResources.filter((r) => relatedIds.has(r.id));
          return {
            _embedded: { items },
            _links: { self: { href: '' } },
            page: {
              number: 1,
              size: items.length,
              totalItems: items.length,
              totalPages: 1,
            },
          } as JsonHalCollection<InventoryResourceModel>;
        }),
        tap((response) => {
          this._initRelationsTab(integrationId, model, response);
          this._initIdentitiesCard(response._embedded.items, model.sampleRelations);
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  private _initIdentitiesCard(
    relatedResources: InventoryResourceModel[],
    sampleRelations?: InventoryResourceGenericRelation[]
  ): void {
    if (!relatedResources.length) {
      return;
    }

    const agentRelationTypes = new Set(['calledBy']);
    const excludedIds = new Set(
      (sampleRelations ?? []).filter((r) => agentRelationTypes.has(r.relationType)).map((r) => r.relatedResourceId)
    );

    const iconMap: Record<string, string> = {
      [ResourceCategory.identity]: 'assets/images/common/user.svg',
      [ResourceCategory.bot]: 'assets/images/logo/langchain.svg',
      [ResourceCategory.generic]: 'assets/images/common/tool.svg',
    };

    const fields: ICardField[] = [];
    relatedResources.forEach((resource) => {
      if (excludedIds.has(resource.id)) {
        return;
      }
      const props = resource.additionalProperties ?? {};
      if (props['privileged'] === undefined && props['managed'] === undefined) {
        return;
      }
      const name = resource.displayName?.split(' (')[0] ?? resource.displayName;
      const accessType = props['accessType'] as string;
      const privileged = props['privileged'] ? 'Yes' : 'No';
      const managed = props['managed'] ? 'Yes' : 'No';
      const icon = iconMap[resource.resourceCategory] ?? getResourceCategoryIconPath(resource.resourceCategory);
      const valueParts = [
        ...(accessType ? [`Type: ${accessType}`] : []),
        `Privileged: ${privileged}`,
        `Managed: ${managed}`,
      ];
      fields.push({
        key: name,
        value: valueParts.join('  |  '),
        disableToggle: true,
        iconPath: icon,
      });
    });
    this._identitiesCardFields.set(fields);
  }

  private _loadMockResourceById(resourceId: string): Observable<InventoryResourceModel> {
    const jsonPath = this._isIamDemoMockResource(resourceId)
      ? 'assets/mockData/iamDemoInventoryMockData.json'
      : 'assets/mockData/rsaInventoryMockData.json';

    return this._httpClient.get<InventoryResourceModel[]>(jsonPath).pipe(
      map((resources) => {
        const found = resources.find((r) => r.id === resourceId);
        if (!found) {
          throw new Error(`Mock resource ${resourceId} not found`);
        }
        return found;
      })
    );
  }

  private _initSinglePage(
    model: InventoryResourceModel,
    resourceId: string,
    integrationId: string,
    viewModel: IInventoryResourceViewModel | undefined
  ): void {
    this._tabs.set([
      { labelPath: 'inventoryResourcePage.overviewTabTitle' },
      { labelPath: 'inventoryResourcePage.relationsTabTitle' },
      { labelPath: 'inventoryResourcePage.violationsTabTitle' },
      { labelPath: 'inventoryResourcePage.hygieneTabTitle' },
    ]);

    this._initCardsData(model, integrationId);
    this._setLinkDisabledStatus();

    const isMock = this._isMockResource(resourceId);

    if (isMock) {
      this._initMockRelationsTab(integrationId, model, resourceId);
      this._initMockViolationsTab(resourceId, model);
      this._isHygieneTabLoading.set(false);
    } else {
      if (model?.sampleRelations.length === 0) {
        this._initRelationsTab(integrationId, model);
      } else {
        const relatedResourceIds = Array.from(new Set(model?.sampleRelations.map((rel) => rel.relatedResourceId)));
        this._inventoryHttpService
          .getInventoryResourcesByIds(relatedResourceIds)
          .pipe(
            tap((response) => this._initRelationsTab(integrationId, model, response)),
            catchError((error) => {
              this._httpErrorHandlerService.handle(error);
              return EMPTY;
            }),
            takeUntilDestroyed(this._destroyRef)
          )
          .subscribe();
      }

      const violationsHygieneFilters = {
        resourceId: [resourceId],
        integration: [integrationId],
        violationStatus: [ViolationStatus.open],
      };

      this._violationsHttpService
        .getViolations(violationsHygieneFilters)
        .pipe(
          tap((violationResponse) =>
            this._initViolationsOrHygieneTab(violationResponse, ViolationsDataType.violations)
          ),
          concatMap(() => this._violationsHttpService.getHygiene(violationsHygieneFilters)),
          tap((violationResponse) => this._initViolationsOrHygieneTab(violationResponse, ViolationsDataType.hygiene)),
          tap(() => {
            this._isViolationsTabLoading.set(false);
            this._isHygieneTabLoading.set(false);
          }),
          catchError((error) => {
            this._httpErrorHandlerService.handle(error);
            return EMPTY;
          }),
          takeUntilDestroyed(this._destroyRef)
        )
        .subscribe();
    }

    const menuActions = viewModel?.menuActions;

    if (menuActions && menuActions.length > 0) {
      this._menuActions.set(structuredClone(menuActions));
    } else if (
      isMock &&
      model.resourceCategory === ResourceCategory.bot &&
      this._featureFlagsService.isFeatureEnabled(
        FeatureFlagTypes.SpmIamDemoAmex
      )
    ) {
      this._menuActions.set([
        {
          name: InventorySupportedActions.KillAgent,
          displayNameLabel: 'commonActions.killswitch',
          disabled: false,
          shouldOpenDialog: true,
        },
      ]);
    } else if (!isMock) {
      const tempView: IInventoryResourceViewModel = {
        id: model.id || '',
        resourceName: '',
        resourceType: '',
        resourceTypeIcon: '',
        environment: '',
        createdBy: '',
        ownedBy: [],
        platform: Platform.powerplatform,
        platformIcon: '',
        businessCriticality: '',
        securityRisk: '',
        creationTime: '',
        resource: model,
        rowId: '',
        clickable: false,
      };

      this._inventoryActionsService
        .setActionsForResources([model], [tempView])
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          complete: () => {
            if (tempView.menuActions) {
              this._menuActions.set(tempView.menuActions);
            }
          },
        });
    }

    this._isPageLoading.set(false);

    const statusLabel: StatusLabelEnum = model?.resourceState
      ? InventoryHelper.getResourceStatusLabel(model?.resourceState.status)
      : StatusLabelEnum.active;
    this._statusLabel.set(statusLabel);

    this._initStatusLabelReason(model);
  }

  private _initCardsData(model: InventoryResourceModel, integrationId: string) {
    this._initInfoCardData(model, integrationId);
    this._initAdditionalInfoCardData(model);
    this._initEnvironmentCardData(model);
    this._initGenerativeAiCardData(model);
    this._initTimelineCardData(model);
  }

  private _initInfoCardData(model: InventoryResourceModel, integrationId: string) {
    const infoCardFields = [
      this._resourceCardFieldBuilder.buildBasicCardFiled('zenityCommon.integration', this._integrationName),
      this._resourceCardFieldBuilder.buildBasicCardFiled(
        'inventoryResourcePage.infoCardResourceCategoryFieldKey',
        getResourceCategoryDisplayName(model.resourceCategory, this._beautifyDisplayStringPipe),
        'commonEntitiesInfo.resourceCategory'
      ),
      this._resourceCardFieldBuilder.buildBasicCardFiled(
        'inventoryResourcePage.infoCardResourceTypeFieldKey',
        buildResourceTypeDisplayName(model.resourceType),
        'commonEntitiesInfo.resourceType',
        getResourceCategoryIconPath(model.resourceCategory)
      ),
    ];

    if (!this._resourcesWithoutPlatformResourceId.includes(model.resourceType)) {
      infoCardFields.push(
        this._resourceCardFieldBuilder.buildBasicCardFiled(
          'inventoryResourcePage.infoCardPlatformResourceIdFieldKey',
          model.platformInfo.resourceId,
          'commonEntitiesInfo.platformResourceId',
          undefined,
          true
        )
      );
    }

    if (this._isCreatedByFieldApplicable(model)) {
      infoCardFields.push(this._getCreatedByField(model, integrationId));
    }

    if (
      this._hasOwnedByRelation(model) ||
      ![ResourceCategory.connector, ResourceCategory.identity].includes(model.resourceCategory)
    ) {
      infoCardFields.push(this._getOwnedByField(model, integrationId));
    }

    if (model.identities.status === InventoryEntityFieldStatus.hasValue) {
      if (this._resourceCardFieldBuilder.isFieldSupported(model.identities.value?.orgWideAccess)) {
        infoCardFields.push(
          this._resourceCardFieldBuilder.buildBasicCardFiled(
            'inventoryResourcePage.infoCardOrgWideAccessFieldKey',
            this._getAccessValue(model.identities.value?.orgWideAccess),
            'commonEntitiesInfo.orgWideAccess'
          )
        );
      }

      if (this._resourceCardFieldBuilder.isFieldSupported(model.identities.value?.publicAccess)) {
        infoCardFields.push(
          this._resourceCardFieldBuilder.buildBasicCardFiled(
            'inventoryResourcePage.infoCardPublicAccessFieldKey',
            this._getAccessValue(model.identities.value?.publicAccess),
            'commonEntitiesInfo.publicAccess'
          )
        );
      }
    }

    const versioningInfo = model.resourceState.versioningInfo;

    if (versioningInfo.status === InventoryEntityFieldStatus.hasValue) {
      infoCardFields.push(
        this._resourceCardFieldBuilder.buildBasicCardFiled(
          'inventoryResourcePage.infoCardResourcePublicationStatusFieldKey',
          this._beautifyDisplayStringPipe.transform(versioningInfo.value?.publicationStatus),
          'commonEntitiesInfo.publicationStatus'
        )
      );

      if (
        versioningInfo.value?.lastVersion.status === InventoryEntityFieldStatus.hasValue &&
        versioningInfo.value?.lastVersion.value?.version.status === InventoryEntityFieldStatus.hasValue
      ) {
        infoCardFields.push(
          this._resourceCardFieldBuilder.buildBasicCardFiled(
            'inventoryResourcePage.infoCardResourceVersionFieldKey',
            versioningInfo.value?.lastVersion.value?.version.value,
            'commonEntitiesInfo.version'
          )
        );
      }
    }

    if (SupportedResourcesCategoriesScores.includes(model.resourceCategory)) {
      infoCardFields.push(
        this._resourceCardFieldBuilder.buildBasicCardFiled(
          'inventoryResourcePage.infoCardResourceBusinessCriticalityFieldKey',
          this._getBusinessCriticalityScore(model),
          'commonEntitiesInfo.businessCriticality'
        ),
        this._resourceCardFieldBuilder.buildBasicCardFiled(
          'inventoryResourcePage.infoCardResourceSecurityRiskFieldKey',
          model.scores == null
            ? this._comingSoonString
            : this._beautifyDisplayStringPipe.transform(model.scores.securityRisk),
          'commonEntitiesInfo.securityRisk'
        )
      );
    }

    switch (model.resourceCategory) {
      case ResourceCategory.automation:
        this._pushAutomationExtraProperties(
          model,
          model.resourceCategoryProperties as InventoryAutomationResourceProperties,
          infoCardFields
        );
        break;
      case ResourceCategory.identity:
        this._pushIdentityExtraProperties(
          model.resourceCategoryProperties as InventoryIdentityResourceProperties,
          infoCardFields
        );
        break;
      case ResourceCategory.connection:
        this._pushConnectionExtraProperties(
          integrationId,
          model.resourceCategoryProperties as InventoryConnectionResourceProperties,
          infoCardFields
        );
        break;
      case ResourceCategory.connector:
        this._pushConnectorExtraProperties(
          model.resourceCategoryProperties as InventoryConnectorResourceProperties,
          infoCardFields
        );
        break;
      case ResourceCategory.application:
        this._pushApplicationExtraProperties(
          model.resourceCategoryProperties as InventoryApplicationResourceProperties,
          infoCardFields
        );
        break;
      case ResourceCategory.environment:
        this._pushEnvironmentExtraProperties(
          model.resourceCategoryProperties as InventoryEnvironmentResourceProperties,
          infoCardFields
        );
        break;
      case ResourceCategory.dataStorage:
        this._pushDataStorageExtraProperties(
          model.resourceCategoryProperties as InventoryDataStorageResourceProperties,
          infoCardFields
        );
        break;
      case ResourceCategory.tools:
        this._pushToolsExtraProperties(
          model.resourceCategoryProperties as InventoryToolsResourceProperties,
          infoCardFields
        );
        break;
      case ResourceCategory.device:
        this._pushDeviceExtraProperties(
          model.resourceCategoryProperties as InventoryDeviceResourceProperties,
          infoCardFields
        );
        break;
      case ResourceCategory.bot:
        this._pushBotExtraProperties(
          model.resourceCategoryProperties as InventoryBotResourceProperties,
          infoCardFields
        );
        break;
      case ResourceCategory.aiExtension:
        // Push extra properties for AI extension once they are added
        break;
    }

    if (model.resourceDescription.status === InventoryEntityFieldStatus.hasValue && model.resourceDescription.value) {
      infoCardFields.push(
        this._resourceCardFieldBuilder.buildBasicCardFiled(
          'inventoryResourcePage.infoCardResourceDescriptionFieldKey',
          model.resourceDescription.value
        )
      );
    }

    if (
      model.resourceCategory === ResourceCategory.bot &&
      (model.resourceCategoryProperties as InventoryBotResourceProperties)?.instructions?.status ===
        InventoryEntityFieldStatus.hasValue
    ) {
      infoCardFields.push(
        this._resourceCardFieldBuilder.buildBasicCardFiled(
          'inventoryResourcePage.infoCardInstructionsFieldKey',
          (model.resourceCategoryProperties as InventoryBotResourceProperties)?.instructions?.value
        )
      );
    }

    if (
      model.resourceCategory === ResourceCategory.bot &&
      (model.resourceCategoryProperties as InventoryBotResourceProperties)?.webBrowsing?.status ===
        InventoryEntityFieldStatus.hasValue
    ) {
      infoCardFields.push(
        this._resourceCardFieldBuilder.buildBasicCardFiled(
          'inventoryResourcePage.infoCardWebBrowsingCapabilityFieldKey',
          (model.resourceCategoryProperties as InventoryBotResourceProperties)?.webBrowsing?.value ? 'Yes' : 'No'
        )
      );
    }

    // TODO: remove when not needed
    if (model.id === '43602d2c7001fd1b76df52a0b74efe86') {
      infoCardFields.push({
        key: 'Is Privileged',
        value: 'True',
        isCopyable: false,
      });
    }

    this._infoCardFields.set(infoCardFields);
  }

  private _initAdditionalInfoCardData(model: InventoryResourceModel) {
    let additionalPropertiesExcludingInstructionsAndWebBrowsingCapability: InventoryAdditionalProperties;

    // This is a temporary fix to exclude the "instructions" and "webBrowsingCapability" fields from the additional properties until it is done on BE
    const shouldExcludeInstructions =
      model.resourceCategory === ResourceCategory.bot && 'instructions' in model.additionalProperties;
    const shouldExcludeWebBrowsingCapability =
      model.resourceCategory === ResourceCategory.bot && 'webBrowsingCapability' in model.additionalProperties;

    if (shouldExcludeInstructions || shouldExcludeWebBrowsingCapability) {
      /* eslint-disable @typescript-eslint/no-unused-vars */
      const { instructions, webBrowsingCapability, ...rest } = model.additionalProperties;
      additionalPropertiesExcludingInstructionsAndWebBrowsingCapability = rest;
    } else {
      additionalPropertiesExcludingInstructionsAndWebBrowsingCapability = model.additionalProperties;
    }

    const additionalInfoCardFields: ICardField[] = [];

    this._pushAdditionalProperties(
      additionalPropertiesExcludingInstructionsAndWebBrowsingCapability,
      additionalInfoCardFields
    );

    const urls = model.platformInfo.urls;
    if (urls.status === InventoryEntityFieldStatus.hasValue && urls.value?.additionalUrls) {
      this._pushAdditionalUrlsProperties(urls.value.additionalUrls, additionalInfoCardFields);
    }

    this._additionalInfoCardFields.set(additionalInfoCardFields);
  }

  private _initEnvironmentCardData(model: InventoryResourceModel) {
    const environmentCardFields = [];

    // If the environment name & type are not available, we don't want to show it
    if (model.environment?.value?.displayName) {
      environmentCardFields.push({
        key: Label.get('inventoryResourcePage.environmentCardNameFieldKey'),
        value: model.environment?.value?.displayName,
        isCopyable: model.environment.status === InventoryEntityFieldStatus.hasValue,
      });
    }

    if (model.environment.status === InventoryEntityFieldStatus.hasValue) {
      environmentCardFields.push({
        key: Label.get('inventoryResourcePage.environmentCardTypeFieldKey'),
        value:
          model.environment.status === InventoryEntityFieldStatus.hasValue
            ? this._beautifyDisplayStringPipe.transform(model.environment.value?.environmentType)
            : this._notAvailableString,
      });
    }

    environmentCardFields.push({
      key: Label.get('inventoryResourcePage.environmentCardPlatformFieldKey'),
      value: getPlatformDisplayName(model.platformInfo.platform),
      iconPath: getPlatformLogoPath(model.platformInfo.platform),
      disableToggle: true,
    });

    // Optional AI Service row
    if (model.aiService) {
      environmentCardFields.push({
        key: Label.get('inventoryResourcePage.environmentCardAiServiceFieldKey'),
        value: getAiServiceDisplayName(model.aiService),
        iconPath: getAiServiceLogoPath(model.aiService),
        disableToggle: true,
      });
    }

    this._environmentCardFields.set(environmentCardFields);
  }

  private _initGenerativeAiCardData(model: InventoryResourceModel) {
    if (model.generativeAiProperties?.status !== InventoryEntityFieldStatus.hasValue) {
      return;
    }

    const generativeAiPropertiesCardFields = [];

    if (model.generativeAiProperties.value?.createdUsingGenerativeAi.status === InventoryEntityFieldStatus.hasValue) {
      generativeAiPropertiesCardFields.push({
        key: Label.get('inventoryResourcePage.generativeAiCardCreatedUsingGenerativeAiFieldKey'),
        value: this._beautifyDisplayStringPipe.transform(
          model.generativeAiProperties.value?.createdUsingGenerativeAi.value
        ),
      });
    }

    this._generativeAiPropertiesCardFields.set(generativeAiPropertiesCardFields);
  }

  private _initTimelineCardData(model: InventoryResourceModel) {
    enum InventoryTimelineEventKey {
      createdAt,
      lastModified,
      lastPublished,
      firstSeen,
      lastRun,
      suspended,
      lastEvaluatedByDlp,
      lastRefreshed,
    }

    interface InventoryTimelineEvent {
      key: InventoryTimelineEventKey;
      date: Date; //cant be undefined even if dateString is specified as used for sorting
      text: string;
      iconPath: string;
      hover?: string;
      dateString?: string;
      infoTooltipText?: string;
      showLightningIcon?: boolean;
      lightningTooltipText?: string;
    }

    const timelineEvents: InventoryTimelineEvent[] = [];

    if (model.platformInfo.creationTime.status === InventoryEntityFieldStatus.hasValue) {
      timelineEvents.push({
        key: InventoryTimelineEventKey.createdAt,
        date: new Date(model.platformInfo.creationTime.value!),
        text: this._buildTimelineEventWithIdentityText(
          'inventoryResourcePage.timelineCardCreatedEvent',
          model.identities.value?.createdBy
        ),
        iconPath: 'assets/images/common/plus.svg',
      });
    }

    if (model.platformInfo.lastModifiedTime.status === InventoryEntityFieldStatus.hasValue) {
      timelineEvents.push({
        key: InventoryTimelineEventKey.lastModified,
        date: new Date(model.platformInfo.lastModifiedTime.value!),
        text: this._buildTimelineEventWithIdentityText(
          'inventoryResourcePage.timelineCardLastModifiedEvent',
          model.identities.value?.lastModifiedBy
        ),
        iconPath: 'assets/images/common/settings-timeline.svg',
      });
    }

    timelineEvents.push({
      key: InventoryTimelineEventKey.firstSeen,
      date: new Date(model.platformInfo.firstSeen),
      text: Label.get('inventoryResourcePage.timelineCardFirstSeenEvent'),
      iconPath: 'assets/images/common/seen.svg',
    });

    const versioningInfo = model.resourceState.versioningInfo;
    if (versioningInfo.status === InventoryEntityFieldStatus.hasValue) {
      const lastVersion = versioningInfo.value?.lastVersion;
      if (
        lastVersion?.status === InventoryEntityFieldStatus.hasValue &&
        lastVersion.value?.publishedTime.status === InventoryEntityFieldStatus.hasValue &&
        lastVersion.value.publishedTime.value
      ) {
        timelineEvents.push({
          key: InventoryTimelineEventKey.lastPublished,
          date: new Date(lastVersion.value.publishedTime.value),
          text: this._buildTimelineEventWithIdentityText(
            'inventoryResourcePage.timelineCardLastPublishedEvent',
            model.identities.value?.lastModifiedBy
          ),
          iconPath: 'assets/images/common/settings-timeline.svg',
        });
      }
    }

    // TODO: Remove after all copilot resources finish scan with new category
    if ('lastPublished' in model.additionalProperties) {
      timelineEvents.push({
        key: InventoryTimelineEventKey.lastPublished,
        date: new Date(model.additionalProperties['lastPublished'] as string),
        text: this._buildTimelineEventWithIdentityText(
          'inventoryResourcePage.timelineCardLastPublishedEvent',
          model.identities.value?.lastModifiedBy
        ),
        iconPath: 'assets/images/common/settings-timeline.svg',
      });
    }

    if (model.resourceType === ResourceType.dataset) {
      let text = Label.get('inventoryResourcePage.timelineCardDatasetLastRefresh');
      let lastRefreshDate: Date;
      let dateString: string | undefined;
      let hover: string | undefined;
      if (DatasetResourceAdditionalPropertiesKeys.lastRefreshTime in model.additionalProperties) {
        lastRefreshDate = new Date(
          model.additionalProperties[DatasetResourceAdditionalPropertiesKeys.lastRefreshTime] as string
        );
      } else {
        text += ` (${Label.get('inventoryResourcePage.timelineCardLastRunNotFoundMessages.noDatafoundUsingFirstSeenAsAncorMessage')})`;
        lastRefreshDate = this._persistenceBranchMerged;
        dateString = Label.get('inventoryResourcePage.timelineCardLastRunNotFoundMessages.possiblyNeverOccuredMessage');
        hover = Label.get(
          'inventoryResourcePage.timelineCardLastRunNotFoundMessages.noDatafoundUsingFirstSeenAsAncorHover'
        );
      }

      timelineEvents.push({
        key: InventoryTimelineEventKey.lastRefreshed,
        date: lastRefreshDate,
        dateString: dateString,
        text: text,
        iconPath: 'assets/images/common/refresh.svg',
        hover: hover,
      });
    }

    if (model.resourceCategory === ResourceCategory.automation) {
      const automationResourceProperties = model.resourceCategoryProperties as InventoryAutomationResourceProperties;
      let text = Label.get('inventoryResourcePage.timelineCardLastRunEvent');
      let lastExecutionDate: Date;
      let dateString: string | undefined;
      let hover: string | undefined;
      if (automationResourceProperties.executionInfo.status !== InventoryEntityFieldStatus.hasValue) {
        text += ` (${Label.get('inventoryResourcePage.timelineCardLastRunNotFoundMessages.noDatafoundUsingFirstSeenAsAncorMessage')})`;
        lastExecutionDate = this._persistenceBranchMerged;
        dateString = Label.get('inventoryResourcePage.timelineCardLastRunNotFoundMessages.possiblyNeverOccuredMessage');
        hover = Label.get(
          'inventoryResourcePage.timelineCardLastRunNotFoundMessages.noDatafoundUsingFirstSeenAsAncorHover'
        );
      } else {
        if (
          automationResourceProperties.executionInfo.value?.lastExecutionStatus.status ===
            InventoryEntityFieldStatus.hasValue &&
          automationResourceProperties.executionInfo.value.lastExecutionTime.status ===
            InventoryEntityFieldStatus.hasValue &&
          automationResourceProperties.executionInfo.value.lastExecutionTime.value
        ) {
          text += ` (${automationResourceProperties.executionInfo.value.lastExecutionStatus.value})`;
          lastExecutionDate = new Date(automationResourceProperties.executionInfo.value.lastExecutionTime.value);
        } else if (
          automationResourceProperties.executionInfo.value?.lastExecutionStatus.status ===
          InventoryEntityFieldStatus.hasValue
        ) {
          text += ` (${automationResourceProperties.executionInfo.value.lastExecutionStatus.value})`;
          lastExecutionDate = this._persistenceBranchMerged;
          dateString = Label.get(
            'inventoryResourcePage.timelineCardLastRunNotFoundMessages.possiblyNeverOccuredMessage'
          );
        } else if (
          automationResourceProperties.executionInfo.value?.lastExecutionTime.status ===
            InventoryEntityFieldStatus.hasValue &&
          automationResourceProperties.executionInfo.value.lastExecutionTime.value
        ) {
          text += ` (${Label.get('inventoryResourcePage.timelineCardLastRunNotFoundMessages.noLastExecutionStatus')})`;
          lastExecutionDate = new Date(automationResourceProperties.executionInfo.value.lastExecutionTime.value);
        } else {
          text += ` (${Label.get('inventoryResourcePage.timelineCardLastRunNotFoundMessages.noDatafoundUsingFirstSeenAsAncorMessage')})`;
          lastExecutionDate = this._persistenceBranchMerged;
          dateString = Label.get(
            'inventoryResourcePage.timelineCardLastRunNotFoundMessages.possiblyNeverOccuredMessage'
          );
          hover = Label.get(
            'inventoryResourcePage.timelineCardLastRunNotFoundMessages.noDatafoundUsingFirstSeenAsAncorHover'
          );
        }
      }

      timelineEvents.push({
        key: InventoryTimelineEventKey.lastRun,
        date: lastExecutionDate,
        dateString: dateString,
        hover: hover,
        text: text,
        iconPath: 'assets/images/common/play.svg',
      });
    }

    if (model.resourceCategory === ResourceCategory.automation) {
      const automationResourceProperties = model.resourceCategoryProperties as InventoryAutomationResourceProperties;
      if (
        automationResourceProperties.suspensionInfo.status === InventoryEntityFieldStatus.hasValue &&
        automationResourceProperties.suspensionInfo.value?.suspensionTime.status ===
          InventoryEntityFieldStatus.hasValue &&
        automationResourceProperties.suspensionInfo.value.suspensionTime.value
      ) {
        timelineEvents.push({
          key: InventoryTimelineEventKey.suspended,
          date: new Date(automationResourceProperties.suspensionInfo.value.suspensionTime.value),
          text: this._buildTimelineSuspensionEventText(
            automationResourceProperties.suspensionInfo.value.suspensionReason
          ),
          iconPath: 'assets/images/common/error.svg',
        });
      }
    }

    if (model.resourceCategory === ResourceCategory.application) {
      const applicationResourceProperties = model.resourceCategoryProperties as InventoryApplicationResourceProperties;
      if (
        applicationResourceProperties.dlpEvaluationInfo.status === InventoryEntityFieldStatus.hasValue &&
        applicationResourceProperties.dlpEvaluationInfo.value?.lastEvaluationTime.status ===
          InventoryEntityFieldStatus.hasValue &&
        applicationResourceProperties.dlpEvaluationInfo.value.lastEvaluationTime.value
      ) {
        let text = Label.get('inventoryResourcePage.timelineCardDlpLastEvaluated');
        if (
          applicationResourceProperties.dlpEvaluationInfo.value.evaluationResult.status ===
          InventoryEntityFieldStatus.hasValue
        ) {
          text += ` (${this._beautifyDisplayStringPipe.transform(applicationResourceProperties.dlpEvaluationInfo.value.evaluationResult.value)})`;
        }
        timelineEvents.push({
          key: InventoryTimelineEventKey.lastEvaluatedByDlp,
          date: new Date(applicationResourceProperties.dlpEvaluationInfo.value.lastEvaluationTime.value),
          text: text,
          iconPath: 'assets/images/common/search.svg',
        });
      }
    }

    if (model.platformInfo.lastRefreshed) {
      timelineEvents.push({
        key: InventoryTimelineEventKey.lastRefreshed,
        date: new Date(model.platformInfo.lastRefreshed),
        text: Label.get('inventoryResourcePage.timelineCardLastRefreshedEvent'),
        iconPath: 'assets/images/common/settings-timeline.svg',
        infoTooltipText: Label.get('inventoryResourcePage.lastRefreshedTooltip'),
        showLightningIcon: model.refreshMethod === RefreshMethod.eventDriven,
        lightningTooltipText: Label.get('inventoryResourcePage.lightningTooltip'),
      });
    }

    timelineEvents.sort(
      (event1: InventoryTimelineEvent, event2: InventoryTimelineEvent) => event1.date.getTime() - event2.date.getTime()
    );

    const timelineItems = timelineEvents.map((event) => ({
      text: event.text,
      // Empty string default for compiler only, we know at runtime there will also be a date value here
      secondaryText: event.dateString ?? this._datePipe.transform(event.date, 'medium') ?? '',
      iconPath: event.iconPath,
      tooltipText: event.hover,
      infoTooltipText: event.infoTooltipText,
      showLightningIcon: event.showLightningIcon,
      lightningTooltipText: event.lightningTooltipText,
    }));

    this._timelineItems.set(timelineItems);
  }

  private _setLinkDisabledStatus(): void {
    // Links were previously disabled for portal resources using: this.linkDisabledTooltip = Label.get("inventoryResourcePage.portalLinkDisabledTooltip")
    this._isLinkDisabled.set(false);
  }

  private _initStatusLabelReason(model: InventoryResourceModel) {
    let label = ''; // defaults to no tooltip

    if (model?.resourceState.statusReason !== undefined && this._statusLabel() === StatusLabelEnum.inactive) {
      // show status reason only if resource is inactive
      label = model.resourceState.statusReason;
    }

    this._statusLabelReason.set(label);
  }

  private _initViolationsOrHygieneTab(
    response: JsonHalCollection<ViolationModel>,
    tabType: ViolationsDataType.violations | ViolationsDataType.hygiene
  ) {
    const cards: RelatedViolationCardComponentInput[] = [];

    response._embedded.items.forEach((violation) => {
      cards.push({
        violationName: violation.rule.displayName,
        ruleCategory: this._beautifyDisplayStringPipe.transform(violation.rule.category),
        // Empty string default for compiler only, we know at runtime there will also be a date value here
        firstSeen: this._datePipe.transform(violation.firstSeen, 'medium') ?? '',
        violationId: violation.id,
        statusLabel: ViolationRowHelpers.getStatusLabel(violation.status),
        severity: violation.rule.severity,
        integrationId: violation.integration.id,
      });
    });

    if (tabType === 'violations') {
      this._relatedViolationsCardsInputs.set(cards);
    }
    if (tabType === 'hygiene') {
      this._relatedHygieneCardsInputs.set(cards);
    }
  }

  private _initRelationsTab(
    integrationId: string,
    model: InventoryResourceModel,
    response?: JsonHalCollection<InventoryResourceModel>
  ) {
    if (response) {
      const relatedResourcesByIds: {
        [id: string]: InventoryResourceModel;
      } = {};
      response._embedded.items.forEach((relatedResource) => {
        relatedResourcesByIds[relatedResource.id] = relatedResource;
      });

      // Keeping order to related resources cards according to order of sampleRelations determined by the backend, it has importance
      model.sampleRelations.forEach((relation) => {
        const relatedResourcesIds = Object.keys(relatedResourcesByIds);
        if (relatedResourcesIds.indexOf(relation.relatedResourceId) >= 0) {
          const relatedResource = relatedResourcesByIds[relation.relatedResourceId];
          this._pushRelatedResourceCard(integrationId, model, relatedResource, relation);
        }
        // resource not found means a broken relation
        // in that case we can/want to show the basic relation fields only
        else {
          this._pushBrokenRelationeCard(integrationId, relation);
        }
      });
      this._updateCategoryCardFilters(model);
    }

    this._isRelationsTabLoading.set(false);
  }

  private _getAccessValue(accessField?: InventoryEntityField<InventoryOrgWideAccessCategory>): string {
    if (accessField?.status !== InventoryEntityFieldStatus.hasValue) return this._notAvailableString;
    if (accessField?.value === InventoryOrgWideAccessCategory.none) return Label.get('common.no');
    return `${Label.get('common.yes')} (${accessField?.value && this._displayCamelCaseStringPipe.transform(accessField?.value)})`;
  }

  private readonly _getBusinessCriticalityScore = (model: InventoryResourceModel): string =>
    model.scores == null || model.scores.businessCriticality === null
      ? this._comingSoonString
      : this._beautifyDisplayStringPipe.transform(model.scores.businessCriticality);

  private _isCreatedByFieldApplicable(model: InventoryResourceModel): boolean {
    const hasCreatedByRelation =
      model.identities.status === InventoryEntityFieldStatus.hasValue &&
      model.identities.value?.createdBy?.status == InventoryEntityFieldStatus.hasValue;
    return (
      hasCreatedByRelation || ![ResourceCategory.connector, ResourceCategory.identity].includes(model.resourceCategory)
    );
  }

  private _getCreatedByField(model: InventoryResourceModel, integrationId: string): ICardField {
    const createdByDisplayName = this._displayNameService.getCreatedByDisplayName(
      model.identities?.value?.createdBy,
      model.additionalProperties
    );
    const hasCreatedByRelation =
      model.identities.status === InventoryEntityFieldStatus.hasValue &&
      model.identities.value?.createdBy?.status == InventoryEntityFieldStatus.hasValue;

    const createdByCardField: ICardField = this._resourceCardFieldBuilder.buildBasicCardFiled(
      'inventoryResourcePage.infoCardCreatedByFieldKey',
      createdByDisplayName,
      'commonEntitiesInfo.createdBy'
    );

    if (hasCreatedByRelation) {
      createdByCardField.internalNavigation = {
        page: '',
        query: {
          integration: integrationId,
          resource: model.identities.value?.createdBy.value?.id,
        },
      };
    }

    return createdByCardField;
  }

  private _getOwnedByField(model: InventoryResourceModel, integrationId: string): ICardField {
    const hasOwnedByRelation = this._hasOwnedByRelation(model);
    const ownedByValue = model.identities?.value?.ownedBy?.value;
    const ownedByDisplayName = hasOwnedByRelation ? ownedByValue?.[0]?.displayName : this._notAvailableString;
    const ownedByCardField: ICardField = this._resourceCardFieldBuilder.buildBasicCardFiled(
      'inventoryResourcePage.infoCardOwnedByFieldKey',
      ownedByDisplayName,
      'commonEntitiesInfo.ownedBy'
    );

    if (hasOwnedByRelation) {
      ownedByCardField.internalNavigation = {
        page: '',
        query: {
          integration: integrationId,
          resource: ownedByValue?.[0].id,
        },
      };
      ownedByCardField.isCopyable = true;
    }

    if (hasOwnedByRelation && ownedByValue && ownedByValue.length > 1) {
      ownedByCardField.additionalValues = ownedByValue.slice(1).map((owner) => owner.displayName);
    }

    return ownedByCardField;
  }

  private _pushAutomationExtraProperties(
    model: InventoryResourceModel,
    extraProperties: InventoryAutomationResourceProperties,
    infoCardFields: ICardField[]
  ) {
    this._pushAutomationTypeCardFieldIfSupported(model, extraProperties.automationType, infoCardFields);
    this._resourceCardFieldBuilder.pushAutomationStatusCardFieldIfSupported(extraProperties, infoCardFields);
    this._resourceCardFieldBuilder.pushAutomationLastRunStatusCardFieldIfSupported(extraProperties, infoCardFields);
    this._resourceCardFieldBuilder.pushCardFieldIfSupported(
      extraProperties.stepsCount,
      (c) => this._beautifyDisplayStringPipe.transform(c.toString()),
      'infoCardAutomationStepsCountFieldKey',
      'automation.stepsCount',
      infoCardFields
    );
    this._resourceCardFieldBuilder.pushCardFieldIfSupported(
      extraProperties.usesAiModel,
      (c) => (c ? Label.get('common.yes') : Label.get('common.no')),
      'infoCardAutomationUsesAiModelFieldKey',
      'automation.usesAiModel',
      infoCardFields
    );
    this._resourceCardFieldBuilder.pushAutomationIsSolutionAwareCardFieldIfSupported(extraProperties, infoCardFields);
    this._resourceCardFieldBuilder.pushAutomationIsBotTriggerCardFieldIfSuported(extraProperties, infoCardFields);
  }

  private _pushIdentityExtraProperties(
    extraProperties: InventoryIdentityResourceProperties,
    infoCardFields: ICardField[]
  ) {
    this._resourceCardFieldBuilder.pushCardFieldIfSupported(
      extraProperties.identityType,
      this._beautifyDisplayStringPipe.transform,
      'infoCardIdentityTypeFieldKey',
      'identity.identityType',
      infoCardFields
    );
    this._pushIdentityProviderIdCardFieldIfSupproted(extraProperties.identityProviderId, infoCardFields);
    this._pushIdentityEmailCardFieldIfSupproted(extraProperties.email, infoCardFields);
    this._pushIdentityDepartmentCardFieldIfSupported(extraProperties.department, infoCardFields);
    this._resourceCardFieldBuilder.pushBooleanCardFieldIfSupported(
      extraProperties.isActive,
      'infoCardIdentityIsActiveFieldKey',
      'identity.isActive',
      infoCardFields
    );
    this._pushIdentityIsExternalCardFieldIfSupported(extraProperties.isExternal, infoCardFields);
    this._pushIdentityIsBuilderCardFieldIfSupported(extraProperties.isBuilder, infoCardFields);
    this._resourceCardFieldBuilder.pushCardFieldIfSupported(
      extraProperties.totalMembersCount,
      (c) => c.toString(),
      'infoCardIdentityTotalMembersCountFieldKey',
      'identity.totalMembersCount',
      infoCardFields
    );
  }

  private _pushConnectionExtraProperties(
    integrationId: string,
    extraProperties: InventoryConnectionResourceProperties,
    infoCardFields: ICardField[]
  ) {
    this._resourceCardFieldBuilder.pushConnectionInUseCardFieldIfSupported(extraProperties, infoCardFields);
    this._resourceCardFieldBuilder.pushConnectionStatusCardFieldIfSupported(extraProperties, infoCardFields);
    this._pushConnectionAccountCardFieldIfSupported(extraProperties.connectionAccount, infoCardFields);
    this._pushConnectionAuthenticationMethodCardFieldIfSupported(extraProperties.authenticationMethod, infoCardFields);
    this._resourceCardFieldBuilder.pushConnectionHostNameCardFieldIfSupported(extraProperties, infoCardFields);
    this._resourceCardFieldBuilder.pushConnectionConnectorLinkCardField(integrationId, extraProperties, infoCardFields);
    this._resourceCardFieldBuilder.pushConnectorProviderCardFieldsIfSupported(
      extraProperties.connector.value ?? {
        provider: {
          status: extraProperties.connector.status,
          value: null,
        },
      },
      infoCardFields
    );
    this._resourceCardFieldBuilder.pushConnectionsIsOnPremisesCardFieldIfSupported(extraProperties, infoCardFields);
  }

  private _pushConnectorExtraProperties(
    extraProperties: InventoryConnectorResourceProperties,
    infoCardFields: ICardField[]
  ) {
    this._resourceCardFieldBuilder.pushConnectorProviderCardFieldsIfSupported(extraProperties, infoCardFields);
    this._resourceCardFieldBuilder.pushConnectorVersionFieldIfSupported(extraProperties, infoCardFields);
    this._resourceCardFieldBuilder.pushConnectorBackendServiceUrlFieldIfSupported(extraProperties, infoCardFields);
    this._resourceCardFieldBuilder.pushConnectorIsDeprecatedFieldIfSupported(extraProperties, infoCardFields);
  }

  private _pushApplicationExtraProperties(
    extraProperties: InventoryApplicationResourceProperties,
    infoCardFields: ICardField[]
  ) {
    this._resourceCardFieldBuilder.pushCardFieldIfSupported(
      extraProperties.applicationType,
      this._displayCamelCaseStringPipe.transform,
      'infoCardApplicationTypeFieldKey',
      'application.applicationType',
      infoCardFields
    );
    this._resourceCardFieldBuilder.pushCardFieldIfSupported(
      extraProperties.version,
      this._displayCamelCaseStringPipe.transform,
      'infoCardApplicationVersionFieldKey',
      'application.version',
      infoCardFields
    );
    this._resourceCardFieldBuilder.pushApplicationQuarantineStatusCardFieldIfSupported(extraProperties, infoCardFields);
  }

  private _pushEnvironmentExtraProperties(
    extraProperties: InventoryEnvironmentResourceProperties,
    infoCardFields: ICardField[]
  ) {
    this._resourceCardFieldBuilder.pushEnvironmentTypeCardFieldIfSupported(extraProperties, infoCardFields);
  }

  private _pushDataStorageExtraProperties(
    extraProperties: InventoryDataStorageResourceProperties,
    infoCardFields: ICardField[]
  ) {
    this._resourceCardFieldBuilder.pushDataStorageTypeCardFieldIfSupported(extraProperties, infoCardFields);
  }

  private _pushDeviceExtraProperties(extraProperties: InventoryDeviceResourceProperties, infoCardFields: ICardField[]) {
    // No device-specific fields currently.
  }

  private _pushToolsExtraProperties(extraProperties: InventoryToolsResourceProperties, infoCardFields: ICardField[]) {
    this._resourceCardFieldBuilder.pushToolsTypeCardFieldIfSupported(extraProperties, infoCardFields);
  }

  private _pushBotExtraProperties(extraProperties: InventoryBotResourceProperties, infoCardFields: ICardField[]) {
    this._resourceCardFieldBuilder.pushBotTypeCardFieldIfSupported(extraProperties, infoCardFields);
    this._resourceCardFieldBuilder.pushBotAutonomousTriggersCountCardFieldIfSupported(extraProperties, infoCardFields);
  }

  private _buildTimelineEventWithIdentityText(
    label: string,
    userField: InventoryEntityField<InventoryResourceKnownRelation> | undefined
  ): string {
    let text = Label.get(label);
    const atLabel = Label.get('common.at');
    const userDisplayName = userField?.value?.displayName;

    if (userDisplayName) {
      const byUserAtTime = ` ${Label.get('common.by')} ${userDisplayName} ${atLabel}`;
      text += byUserAtTime;
    } else {
      const atTime = ` ${atLabel}`;
      text += atTime;
    }

    return text;
  }

  private _buildTimelineSuspensionEventText(reason: InventoryEntityField<string>): string {
    let text = Label.get('inventoryResourcePage.timelineCardSuspensionEvent');
    if (reason.status === InventoryEntityFieldStatus.hasValue) {
      // convert reason from PascalCase to normal string
      text += ` ${Label.get('common.dueTo')} "${this._beautifyDisplayStringPipe.transform(reason.value)}"`;
    }
    text += ` ${Label.get('common.at')}`;

    return text;
  }

  private _updateCategoryCardFilters(model: InventoryResourceModel) {
    let category: keyof typeof model.relatedResourcesSummary;
    let totalRelatedResources = 0;
    let shouldShowRelatedResourcesDisclaimer = false;

    const resourceCategoryFilters = [];

    for (category in model.relatedResourcesSummary) {
      const realRelatedResourcesCount = model.relatedResourcesSummary[category];

      if (realRelatedResourcesCount) {
        const categoryDisplay = this._displayCamelCaseStringPipe.transform(category.toString());
        totalRelatedResources += realRelatedResourcesCount;
        const presentedRelatedResourcesCount = model.sampleRelations.filter(
          (relation) => relation.relatedResourceCategory == category
        ).length;

        if (realRelatedResourcesCount > presentedRelatedResourcesCount) {
          shouldShowRelatedResourcesDisclaimer = true;
        }

        resourceCategoryFilters.push({
          id: category.toString(),
          value: categoryDisplay,
          displayName: categoryDisplay,
          disabled: false,
          numOfItems: realRelatedResourcesCount,
        } as ISelectOption);
      }
    }

    this._resourceCategoryFilters.set(resourceCategoryFilters);
    this._totalRelatedResources.set(totalRelatedResources);

    const sampleRelatedResourcesDisclaimer = shouldShowRelatedResourcesDisclaimer
      ? Label.get('inventoryResourcePage.resourcesRelationsCardShowingSampleDisclaimer')
      : '';

    this._sampleRelatedResourcesDisclaimer.set(sampleRelatedResourcesDisclaimer);
  }

  private _pushRelatedResourceCard(
    integrationId: string,
    model: InventoryResourceModel,
    relatedResource: InventoryResourceModel,
    relation: InventoryResourceGenericRelation
  ) {
    const relationExtraFields: ICardField[] = [];

    if (relatedResource.resourceCategory === ResourceCategory.environment) {
      const envProps = relatedResource.resourceCategoryProperties as InventoryEnvironmentResourceProperties;
      this._updateAgentLinkForEnvironment(envProps?.environmentType?.value);
    }

    switch (relatedResource.resourceCategory) {
      case ResourceCategory.automation:
        this._pushRelatedAutomationExtraProperties(
          model,
          relatedResource.resourceCategoryProperties as InventoryAutomationResourceProperties,
          relationExtraFields
        );
        break;
      case ResourceCategory.identity:
        this._pushRelatedIdentityExtraProperties(
          relatedResource.resourceCategoryProperties as InventoryIdentityResourceProperties,
          relationExtraFields
        );
        break;
      case ResourceCategory.connection:
        this._pushRelatedConnectionExtraProperties(
          integrationId,
          relatedResource.resourceCategoryProperties as InventoryConnectionResourceProperties,
          relationExtraFields
        );
        break;
      case ResourceCategory.connector:
        this._pushRelatedConnectorExtraProperties(
          relatedResource.resourceCategoryProperties as InventoryConnectorResourceProperties,
          relationExtraFields
        );
        break;
      case ResourceCategory.dataStorage:
        this._pushRelatedDataStorageExtraProperties(
          relatedResource.resourceCategoryProperties as InventoryDataStorageResourceProperties,
          relationExtraFields
        );
        break;
      case ResourceCategory.tools:
        this._pushRelatedToolsExtraProperties(
          relatedResource.resourceCategoryProperties as InventoryToolsResourceProperties,
          relationExtraFields
        );
        break;
      case ResourceCategory.device:
        this._pushRelatedDeviceExtraProperties(
          relatedResource.resourceCategoryProperties as InventoryDeviceResourceProperties,
          relationExtraFields
        );
        break;
      case ResourceCategory.bot:
        this._pushRelatedBotExtraProperties(
          relatedResource.resourceCategoryProperties as InventoryBotResourceProperties,
          relationExtraFields
        );
        break;
      case ResourceCategory.aiExtension:
        // Push extra properties for AI extension once they are added
        break;
    }

    const relationAdditionalInfoFields: ICardField[] = [];
    this._pushAdditionalProperties(relation.additionalProperties, relationAdditionalInfoFields);

    const relatedResourceInfo: RelatedResourceInfo = {
      resourceName: relatedResource.displayName,
      resourceId: relatedResource.id,
      resourceType: buildResourceTypeDisplayName(relatedResource.resourceType),
      resourceUrlPath: relation._links.self.href,
      resourceStatus: relatedResource.resourceState
        ? InventoryHelper.getResourceStatusLabel(relatedResource.resourceState.status)
        : StatusLabelEnum.active,
      creationTime: relatedResource.platformInfo.creationTime,
      createdByIdentityName:
        relatedResource.identities.value?.createdBy?.status == InventoryEntityFieldStatus.hasValue &&
        relatedResource.identities.value?.createdBy.value
          ? (relatedResource.identities.value?.createdBy.value?.displayName as string)
          : this._notAvailableString,
      createdByIdentityId: relatedResource.identities.value?.createdBy?.value?.id,
      extraFields: relationExtraFields,
    };

    const connType =
      relation.additionalProperties?.['connectionType'];
    const relTypeDisplay =
      this._displayCamelCaseStringPipe.transform(
        relation.relationType
      ) + (connType ? ` (${connType})` : '');

    const resourceToAdd = {
      relatedResourceInfo: relatedResourceInfo,
      resourceCategory: this._displayCamelCaseStringPipe.transform(relatedResource.resourceCategory.toString()),
      relationType: relTypeDisplay,
      isVisible: true,
      integrationId: integrationId,
      additionalInfoFields: relationAdditionalInfoFields,
    };

    this._relatedResourcesCardsInputs.update((prev) => [...prev, resourceToAdd]);
  }

  private _pushBrokenRelationeCard(integrationId: string, relation: InventoryResourceGenericRelation) {
    const relationAdditionalInfoFields: ICardField[] = [];
    this._pushAdditionalProperties(relation.additionalProperties, relationAdditionalInfoFields);

    const resourceToAdd = {
      relatedResourceInfo: undefined,
      resourceCategory: this._displayCamelCaseStringPipe.transform(relation.relatedResourceCategory.toString()),
      relationType: this._displayCamelCaseStringPipe.transform(relation.relationType),
      isVisible: true,
      integrationId: integrationId,
      additionalInfoFields: relationAdditionalInfoFields,
    };

    this._relatedResourcesCardsInputs.update((prev) => [...prev, resourceToAdd]);
  }

  private _hasOwnedByRelation(model: InventoryResourceModel): boolean {
    return (
      model.identities.status === InventoryEntityFieldStatus.hasValue &&
      model.identities.value?.ownedBy?.status == InventoryEntityFieldStatus.hasValue &&
      model.identities.value.ownedBy.value?.length !== undefined &&
      model.identities.value.ownedBy.value.length > 0
    );
  }

  private _pushRelatedAutomationExtraProperties(
    model: InventoryResourceModel,
    extraProperties: InventoryAutomationResourceProperties,
    cardFields: ICardField[]
  ) {
    this._pushAutomationTypeCardFieldIfSupported(model, extraProperties.automationType, cardFields);
  }

  private _pushRelatedIdentityExtraProperties(
    extraProperties: InventoryIdentityResourceProperties,
    cardFields: ICardField[]
  ) {
    this._pushIdentityProviderIdCardFieldIfSupproted(extraProperties.identityProviderId, cardFields);
    this._pushIdentityEmailCardFieldIfSupproted(extraProperties.email, cardFields);
    this._pushIdentityIsExternalCardFieldIfSupported(extraProperties.isExternal, cardFields);
  }

  private _pushRelatedConnectionExtraProperties(
    integrationId: string,
    extraProperties: InventoryConnectionResourceProperties,
    cardFields: ICardField[]
  ) {
    this._resourceCardFieldBuilder.pushConnectionInUseCardFieldIfSupported(extraProperties, cardFields);
    this._pushConnectionAccountCardFieldIfSupported(extraProperties.connectionAccount, cardFields);
    this._resourceCardFieldBuilder.pushConnectionHostNameCardFieldIfSupported(extraProperties, cardFields);
    this._resourceCardFieldBuilder.pushConnectionConnectorLinkCardField(integrationId, extraProperties, cardFields);
  }

  private _pushRelatedConnectorExtraProperties(
    extraProperties: InventoryConnectorResourceProperties,
    cardFields: ICardField[]
  ) {
    this._resourceCardFieldBuilder.pushConnectorProviderCardFieldsIfSupported(extraProperties, cardFields);
    this._resourceCardFieldBuilder.pushConnectorBackendServiceUrlFieldIfSupported(extraProperties, cardFields);
    this._resourceCardFieldBuilder.pushConnectorIsDeprecatedFieldIfSupported(extraProperties, cardFields);
  }

  private _pushRelatedDataStorageExtraProperties(
    extraProperties: InventoryDataStorageResourceProperties,
    cardFields: ICardField[]
  ) {
    this._resourceCardFieldBuilder.pushDataStorageTypeCardFieldIfSupported(extraProperties, cardFields);
  }

  private _pushRelatedDeviceExtraProperties(
    extraProperties: InventoryDeviceResourceProperties,
    cardFields: ICardField[]
  ) {
    this._resourceCardFieldBuilder.pushDeviceTypeCardFieldIfSupported(extraProperties, cardFields);
  }

  private _pushRelatedToolsExtraProperties(
    extraProperties: InventoryToolsResourceProperties,
    cardFields: ICardField[]
  ) {
    this._resourceCardFieldBuilder.pushToolsTypeCardFieldIfSupported(extraProperties, cardFields);
  }

  private _pushRelatedBotExtraProperties(extraProperties: InventoryBotResourceProperties, cardFields: ICardField[]) {
    this._resourceCardFieldBuilder.pushBotTypeCardFieldIfSupported(extraProperties, cardFields);
  }

  private _pushAutomationTypeCardFieldIfSupported(
    model: InventoryResourceModel,
    automationType: InventoryEntityField<string>,
    cardFields: ICardField[]
  ) {
    // Workato recipe has no automation type
    if (model?.platformInfo.platform !== Platform.workato) {
      this._resourceCardFieldBuilder.pushCardFieldIfSupported(
        automationType,
        this._beautifyDisplayStringPipe.transform,
        'infoCardAutomationTypeFieldKey',
        'automation.automationType',
        cardFields
      );
    }
  }

  private _pushIdentityProviderIdCardFieldIfSupproted(
    identityProviderId: InventoryEntityField<string>,
    cardFields: ICardField[]
  ) {
    this._resourceCardFieldBuilder.pushCardFieldIfSupported(
      identityProviderId,
      (id) => id,
      'infoCardIdentityProviderIdFieldKey',
      'identity.identityProviderId',
      cardFields
    );
  }

  private _pushIdentityEmailCardFieldIfSupproted(email: InventoryEntityField<string>, cardFields: ICardField[]) {
    this._resourceCardFieldBuilder.pushCardFieldIfSupported(
      email,
      (e) => e,
      'infoCardIdentityEmailFieldKey',
      'identity.email',
      cardFields
    );
  }

  private _pushIdentityDepartmentCardFieldIfSupported(
    department: InventoryEntityField<string>,
    cardFields: ICardField[]
  ) {
    this._resourceCardFieldBuilder.pushCardFieldIfSupported(
      department,
      (d) => d,
      'infoCardIdentityDepartmentFieldKey',
      'identity.department',
      cardFields
    );
  }

  private _pushIdentityIsExternalCardFieldIfSupported(
    isExternal: InventoryEntityField<boolean>,
    cardFields: ICardField[]
  ) {
    this._resourceCardFieldBuilder.pushBooleanCardFieldIfSupported(
      isExternal,
      'infoCardIdentityIsExternalFieldKey',
      'identity.isExternal',
      cardFields
    );
  }

  private _pushIdentityIsBuilderCardFieldIfSupported(
    isBuilder: InventoryEntityField<boolean>,
    cardFields: ICardField[]
  ) {
    this._resourceCardFieldBuilder.pushBooleanCardFieldIfSupported(
      isBuilder,
      'infoCardIdentityIsBuilderFieldKey',
      'identity.isBuilder',
      cardFields
    );
  }

  private _pushConnectionAccountCardFieldIfSupported(
    connectionAccount: InventoryEntityField<string>,
    cardFields: ICardField[]
  ) {
    this._resourceCardFieldBuilder.pushCardFieldIfSupported(
      connectionAccount,
      (a) => a,
      'infoCardConnectionAccountFieldKey',
      'connection.connectionAccount',
      cardFields
    );
  }

  private _pushConnectionAuthenticationMethodCardFieldIfSupported(
    authenticationMethod: InventoryEntityField<string>,
    cardFields: ICardField[]
  ) {
    this._resourceCardFieldBuilder.pushCardFieldIfHasValue(
      authenticationMethod,
      (a) => a,
      'infoCardConnectionAuthenticationMethodFieldKey',
      'connection.authenticationMethod',
      cardFields
    );
  }

  private _pushAdditionalUrlsProperties(additionalUrls: InventoryResourceUrl[], cardFieldsForPush: ICardField[]) {
    for (const additionalUrlIndex in additionalUrls) {
      const additionalUrl = additionalUrls[additionalUrlIndex];
      cardFieldsForPush.push({
        key: additionalUrl.displayName,
        value: additionalUrl.url,
        valueLink: additionalUrl.url,
      });
    }
  }

  private _pushAdditionalProperties(
    additionalPropertiesModel: InventoryAdditionalProperties,
    cardFieldsForPush: ICardField[]
  ) {
    for (const additionalPropertyName in additionalPropertiesModel) {
      if (this._hiddenAdditionalProperties.includes(additionalPropertyName)) {
        continue;
      }
      const additionalPropertyRawValue = additionalPropertiesModel[additionalPropertyName];
      let additionalPropertyDisplayValue = this._notAvailableString;
      // TODO: Remove after demoWebsiteUrl is moved to Additional Urls property
      let additionalPropertyLink: string | undefined = undefined;

      // TODO: Remove after demoWebsiteUrl is moved to Additional Urls property
      if (additionalPropertyName === 'demoWebsiteUrl') {
        additionalPropertyLink = additionalPropertyRawValue as string;
      }
      if (typeof additionalPropertyRawValue === 'string') {
        additionalPropertyDisplayValue = this._shouldUseBeautifyCamelCase(additionalPropertyRawValue)
          ? beautifyCamelCaseString(additionalPropertyRawValue)
          : additionalPropertyRawValue;
      } else if (typeof additionalPropertyRawValue === 'boolean') {
        additionalPropertyDisplayValue = Label.get(additionalPropertyRawValue ? 'common.yes' : 'common.no');
      } else if (typeof additionalPropertyRawValue === 'number') {
        additionalPropertyDisplayValue = additionalPropertyRawValue.toString();
      } else if (additionalPropertyRawValue != null) {
        throw TypeError(
          `Unsupported type of Inventory additional property. Property Name: ${additionalPropertyName}, Unsupported Type: ${typeof additionalPropertyRawValue}`
        );
      }

      // TODO: Remove after demoWebsiteUrl is moved to Additional Urls property
      if (additionalPropertyName !== 'lastPublished') {
        cardFieldsForPush.push({
          key: this._displayCamelCaseStringPipe.transform(additionalPropertyName),
          value: additionalPropertyDisplayValue,
          valueLink: additionalPropertyLink,
        });
      }
    }
  }

  private _shouldUseBeautifyCamelCase(additionalPropertyRawValue: string): boolean {
    return [
      ...(Object.values(PowerplatformCopilotPluginType) as string[]),
      ...(Object.values(PowerPlatformCopilotActionType) as string[]),
      ...(Object.values(PowerPlatformCopilotActionAuthMode) as string[]),
    ].includes(additionalPropertyRawValue);
  }

  private _setAgentLinkIfApplicable(resourceType: ResourceType, resourceId: string, aiService?: AiService) {
    this._pendingAgentLinkResourceId = null;
    this._agentLink.set(null);
    this._isAgentLinkDisabled.set(false);
    this._agentLinkDisabledTooltip.set('');

    const featureFlagEnabled = this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.EnterpriseAiDetection);
    const isSupportedResourceType =
      resourceType === ResourceType.copilot ||
      resourceType === ResourceType.agent ||
      resourceType === ResourceType.vertex_agent ||
      resourceType === ResourceType.agent_runtime ||
      resourceType === ResourceType.gpt;
    const isSupportedAiService = aiService && (AIDR_SUPPORTED_SERVICES as string[]).includes(aiService);

    if (!featureFlagEnabled || !isSupportedResourceType || !isSupportedAiService) {
      return;
    }

    const agentLink: RouterNavigationParams = {
      commands: ['app/ai-activity'],
      queryParams: { q: `agent:${resourceId}` },
      queryParamsHandling: 'replace',
    };

    this._agentLink.set(agentLink);

    // Only apply environment-based disable logic for Copilot Studio agents
    // Enable by default - only disable if we explicitly see a non-production environment
    // (environment relation may not be in the sample, so we can't assume non-production)
    if (resourceType === ResourceType.copilot) {
      this._pendingAgentLinkResourceId = resourceId;
    }
  }

  private _updateAgentLinkForEnvironment(environmentType: string | null | undefined) {
    if (!this._pendingAgentLinkResourceId) {
      return;
    }

    const envTypeLower = environmentType?.toLowerCase();
    const isDisabledEnvironment = envTypeLower && RUNTIME_ACTIVITY_DISABLED_ENVIRONMENTS.includes(envTypeLower);

    if (isDisabledEnvironment) {
      this._isAgentLinkDisabled.set(true);
      this._agentLinkDisabledTooltip.set(Label.get('inventoryResourcePage.runtimeActivityDisabledNonProduction'));
    }
  }

  private _closeWindow() {
    this._sidePanelService.close();
  }
}
